import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { 
  ArrowRight, Package, Percent, Zap, Shield, Star,
  CheckCircle, TrendingUp, Globe, Instagram, Newspaper, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://businessmintpr.com';

// Schema.org structured data for Bundles Service
const bundlesSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "PR Bundle Packages",
  "description": "Save up to 35% with curated PR bundles combining digital PR, social media, and print coverage for maximum impact.",
  "provider": {
    "@type": "Organization",
    "name": "BusinessMint PR",
    "url": SITE_URL
  },
  "serviceType": "Bundled PR Services",
  "areaServed": {
    "@type": "Country",
    "name": "India"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "PR Bundle Packages",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Multi-Publication Packs",
          "description": "Get featured in multiple digital PR publications at discounted rates"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "PR + Social Combos",
          "description": "Combine digital PR with social media promotions for maximum reach"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Launch Packages",
          "description": "Complete coverage packages for product and brand launches"
        }
      }
    ]
  }
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": SITE_URL
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Services",
      "item": `${SITE_URL}/marketplace`
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": "Bundles",
      "item": `${SITE_URL}/services/bundles`
    }
  ]
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is included in a bundle?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Each bundle includes a carefully curated mix of services. This could be multiple publication placements, a combination of digital PR and social media, or a full launch package with PR, social, and print coverage. Check each bundle's details for specific inclusions."
      }
    },
    {
      "@type": "Question",
      "name": "How much can I save with bundles?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Bundle savings typically range from 15% to 35% compared to purchasing each service separately. The more comprehensive the bundle, the higher the savings."
      }
    },
    {
      "@type": "Question",
      "name": "Can I customize a bundle?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes! While we offer pre-configured bundles, you can also contact us for custom bundle requests. We can create a tailored package based on your specific needs and budget."
      }
    },
    {
      "@type": "Question",
      "name": "How does delivery work for bundles?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "All services in a bundle are coordinated for optimal timing. You can track everything from a single dashboard. We ensure that multi-channel campaigns are synced for maximum impact."
      }
    }
  ]
};

const Bundles = () => {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    fetchBundles();
  }, []);

  const fetchBundles = async () => {
    try {
      const res = await fetch(`${API_URL}/api/bundles`);
      const data = await res.json();
      setBundles(data.slice(0, 6));
    } catch (err) {
      console.error('Error fetching bundles:', err);
    } finally {
      setLoading(false);
    }
  };

  const bundleTypes = [
    {
      icon: Globe,
      name: 'Multi-Publication Packs',
      description: 'Get featured in multiple digital PR publications at once',
      savings: 'Save up to 25%'
    },
    {
      icon: TrendingUp,
      name: 'PR + Social Combos',
      description: 'Combine digital PR with social media promotions',
      savings: 'Save up to 30%'
    },
    {
      icon: Package,
      name: 'Launch Packages',
      description: 'Complete coverage packages for product/brand launches',
      savings: 'Save up to 35%'
    }
  ];

  const features = [
    {
      icon: Percent,
      title: 'Maximum Savings',
      description: 'Save up to 35% compared to buying services individually.'
    },
    {
      icon: Star,
      title: 'Curated Selection',
      description: 'Hand-picked combinations that work best together.'
    },
    {
      icon: Zap,
      title: 'Coordinated Delivery',
      description: 'All services delivered in sync for maximum impact.'
    },
    {
      icon: Shield,
      title: 'Single Order',
      description: 'One checkout, one payment, one tracking dashboard.'
    }
  ];

  const benefits = [
    'Significant cost savings over individual purchases',
    'Coordinated multi-channel campaigns',
    'Simplified ordering and tracking',
    'Strategic combinations for maximum impact',
    'Perfect for launches, campaigns, and ongoing PR',
    'Custom bundle requests available'
  ];

  const faqs = [
    {
      question: 'What is included in a bundle?',
      answer: 'Each bundle includes a carefully curated mix of services. This could be multiple publication placements, a combination of digital PR and social media, or a full launch package with PR, social, and print coverage. Check each bundle\'s details for specific inclusions.'
    },
    {
      question: 'How much can I save with bundles?',
      answer: 'Bundle savings typically range from 15% to 35% compared to purchasing each service separately. The more comprehensive the bundle, the higher the savings.'
    },
    {
      question: 'Can I customize a bundle?',
      answer: 'Yes! While we offer pre-configured bundles, you can also contact us for custom bundle requests. We can create a tailored package based on your specific needs and budget.'
    },
    {
      question: 'How does delivery work for bundles?',
      answer: 'All services in a bundle are coordinated for optimal timing. You can track everything from a single dashboard. We ensure that multi-channel campaigns are synced for maximum impact.'
    }
  ];

  const getServiceIcon = (type) => {
    if (type?.includes('social')) return <Instagram className="w-5 h-5" />;
    if (type?.includes('print')) return <Newspaper className="w-5 h-5" />;
    return <Globe className="w-5 h-5" />;
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>PR Bundles & Packages India | Save Up to 35% | BusinessMint PR</title>
        <meta name="title" content="PR Bundles & Packages India | Save Up to 35% | BusinessMint PR" />
        <meta name="description" content="Get maximum value with curated PR bundles. Combine digital PR, social media, and print coverage. Save up to 35% on multi-channel campaigns. 20+ bundle options available." />
        <meta name="keywords" content="PR bundles India, PR packages, media coverage bundle, digital PR package, social media bundle, launch package, discounted PR, multi-channel PR" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="author" content="BusinessMint PR" />
        <link rel="canonical" href={`${SITE_URL}/services/bundles`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/services/bundles`} />
        <meta property="og:title" content="PR Bundles & Packages | Save Up to 35%" />
        <meta property="og:description" content="Maximum value with curated PR bundles. Digital PR + social media + print coverage combos at discounted prices." />
        <meta property="og:site_name" content="BusinessMint PR" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`${SITE_URL}/services/bundles`} />
        <meta name="twitter:title" content="PR Bundles & Packages | BusinessMint PR" />
        <meta name="twitter:description" content="Save up to 35% with curated PR bundles. Multi-publication packs, PR + Social combos, launch packages." />
        
        {/* Schema.org Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(bundlesSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#0d0d0d] py-20 md:py-32">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/30 via-transparent to-[#D4AF37]/10"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#0d0d0d] text-xs font-manrope uppercase tracking-widest font-bold mb-6">
              <Percent className="w-4 h-4" />
              Save Up to 35%
            </div>
            
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-white">
              PR{' '}
              <span className="text-[#D4AF37]">Bundles</span>{' '}
              & Packages
            </h1>
            
            <p className="font-manrope text-lg md:text-xl text-gray-400 mb-10 max-w-2xl">
              Get maximum value with our curated bundles. Combine digital PR, social media, and print coverage in smart packages designed for impact.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/marketplace?type=bundles"
                className="btn-shimmer inline-flex items-center justify-center gap-3 px-10 py-5 font-manrope text-sm uppercase tracking-widest"
              >
                View All Bundles
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="#bundle-types"
                className="inline-flex items-center justify-center gap-3 px-10 py-5 border-2 border-[#D4AF37]/50 text-white font-manrope text-sm uppercase tracking-widest hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all"
              >
                See Bundle Types
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#D4AF37] py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="font-mono text-3xl font-bold text-[#0d0d0d]">35%</div>
              <div className="font-manrope text-sm text-[#0d0d0d]/70">Max Savings</div>
            </div>
            <div>
              <div className="font-mono text-3xl font-bold text-[#0d0d0d]">20+</div>
              <div className="font-manrope text-sm text-[#0d0d0d]/70">Bundle Options</div>
            </div>
            <div>
              <div className="font-mono text-3xl font-bold text-[#0d0d0d]">4</div>
              <div className="font-manrope text-sm text-[#0d0d0d]/70">Service Types</div>
            </div>
            <div>
              <div className="font-mono text-3xl font-bold text-[#0d0d0d]">1</div>
              <div className="font-manrope text-sm text-[#0d0d0d]/70">Single Dashboard</div>
            </div>
          </div>
        </div>
      </section>

      {/* Bundle Types Section */}
      <section id="bundle-types" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#1a1a1a]">
              Bundle Categories
            </h2>
            <p className="font-manrope text-[#737373] max-w-2xl mx-auto">
              Choose from strategically designed packages for every need
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {bundleTypes.map((type, index) => {
              const Icon = type.icon;
              return (
                <div key={index} className="group relative bg-[#fafafa] border border-[#e5e5e5] p-8 hover:border-[#D4AF37] transition-all overflow-hidden">
                  <div className="absolute top-4 right-4 px-3 py-1 bg-[#D4AF37] text-[#0d0d0d] text-xs font-bold">
                    {type.savings}
                  </div>
                  <div className="w-16 h-16 bg-[#0d0d0d] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#D4AF37] transition-colors">
                    <Icon className="w-8 h-8 text-[#D4AF37] group-hover:text-[#0d0d0d] transition-colors" />
                  </div>
                  <h3 className="font-playfair text-xl font-bold mb-3 text-[#1a1a1a]">{type.name}</h3>
                  <p className="font-manrope text-sm text-[#737373] mb-6">{type.description}</p>
                  <Link
                    to="/marketplace?type=bundles"
                    className="inline-flex items-center gap-2 text-[#D4AF37] font-manrope text-sm font-semibold hover:gap-3 transition-all"
                  >
                    View Bundles <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#f5f5f5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#1a1a1a]">
              Why Buy Bundles?
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white border border-[#e5e5e5] p-6 hover:border-[#D4AF37] transition-colors">
                  <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <h3 className="font-playfair text-lg font-bold mb-2 text-[#1a1a1a]">{feature.title}</h3>
                  <p className="font-manrope text-sm text-[#737373]">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits & Featured Bundles */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-6 text-[#1a1a1a]">
                Bundle Benefits
              </h2>
              <p className="font-manrope text-[#737373] mb-8">
                Get more value, better coordination, and maximum impact with our curated packages.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="font-manrope text-[#525252]">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <Link
                to="/marketplace?type=bundles"
                className="inline-flex items-center gap-2 mt-8 px-8 py-4 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-sm uppercase tracking-widest font-semibold hover:bg-[#B8960F] transition-colors"
              >
                Explore Bundles
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="bg-[#0d0d0d] p-8 border border-[#D4AF37]/20">
              <h3 className="font-playfair text-xl font-bold text-white mb-6">Popular Bundles</h3>
              {loading ? (
                <div className="text-gray-500">Loading...</div>
              ) : bundles.length > 0 ? (
                <div className="space-y-4">
                  {bundles.map((bundle) => (
                    <Link
                      key={bundle.id}
                      to={`/bundle/${bundle.slug}`}
                      className="block p-4 bg-[#1a1a1a] border border-[#D4AF37]/10 hover:border-[#D4AF37]/50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-playfair font-bold text-white">{bundle.name}</div>
                        {bundle.discount_percent > 0 && (
                          <span className="px-2 py-1 bg-[#D4AF37] text-[#0d0d0d] text-xs font-bold">
                            {bundle.discount_percent}% OFF
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mb-3 line-clamp-2">{bundle.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {bundle.items?.slice(0, 3).map((item, idx) => (
                            <div key={idx} className="w-6 h-6 bg-[#D4AF37]/20 rounded flex items-center justify-center">
                              {getServiceIcon(item.type)}
                            </div>
                          ))}
                          {bundle.items?.length > 3 && (
                            <span className="text-xs text-gray-500">+{bundle.items.length - 3}</span>
                          )}
                        </div>
                        <div className="font-mono text-lg font-bold text-[#D4AF37]">
                          ₹{bundle.bundle_price?.toLocaleString()}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500">Bundles coming soon!</p>
                </div>
              )}
              <Link
                to="/marketplace?type=bundles"
                className="block w-full mt-6 py-3 text-center text-[#D4AF37] font-manrope text-sm uppercase tracking-widest hover:text-white transition-colors"
              >
                View All Bundles →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#f5f5f5]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#1a1a1a]">
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-[#e5e5e5] bg-white">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-[#fafafa] transition-colors"
                >
                  <span className="font-manrope font-semibold text-[#1a1a1a] pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#737373] flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="font-manrope text-sm text-[#737373]">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#D4AF37]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-6 text-[#0d0d0d]">
            Ready to Save Big?
          </h2>
          <p className="font-manrope text-[#0d0d0d]/80 mb-8 max-w-2xl mx-auto">
            Explore our curated bundles and get maximum value for your PR investment.
          </p>
          <Link
            to="/marketplace?type=bundles"
            className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-[#0d0d0d] text-white font-manrope text-sm uppercase tracking-widest hover:bg-black transition-all"
          >
            Browse Bundles
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Bundles;
